import apiService from './api';

class RAGService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 30000; // 30 seconds cache for responses
  }

  // Get cached response
  getCachedResponse(query) {
    const cached = this.cache.get(query);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.response;
    }
    return null;
  }

  // Cache response
  setCachedResponse(query, response) {
    this.cache.set(query, {
      response,
      timestamp: Date.now()
    });
  }

  // Process RAG query
  async processQuery(message, context = {}) {
    try {
      // Check cache first
      const cached = this.getCachedResponse(message);
      if (cached) {
        console.log('🎯 Using cached RAG response');
        return cached;
      }

      console.log('🤖 Processing RAG query:', message);

      // Get user location for distance calculation
      const locationParams = this.getLocationParams();

      const response = await apiService.post('/customer/rag/query', {
        message,
        context
      }, {
        params: locationParams
      });

      if (response.data.success) {
        const result = {
          response: response.data.data.response,
          queryType: response.data.data.queryType,
          processingTime: response.data.data.processingTime,
          isRAG: true
        };

        // Cache the response
        this.setCachedResponse(message, result);

        return result;
      } else {
        throw new Error(response.data.message || 'RAG query failed');
      }
    } catch (error) {
      console.error('RAG Service Error:', error);
      
      // Return fallback response
      return {
        response: "I'm here to help you with beauty services and bookings. How can I assist you today?",
        queryType: 'fallback',
        processingTime: 0,
        isRAG: false,
        error: error.message
      };
    }
  }

  // Check RAG system status
  async getStatus() {
    try {
      const response = await apiService.get('/customer/rag/status');
      
      if (response.data.success) {
        return {
          status: response.data.data.systemStatus,
          cacheSize: response.data.data.cacheSize,
          dataAvailable: response.data.data.dataAvailable
        };
      } else {
        throw new Error(response.data.message || 'Status check failed');
      }
    } catch (error) {
      console.error('RAG Status Error:', error);
      return {
        status: 'error',
        cacheSize: 0,
        dataAvailable: { categories: 0, services: 0, providers: 0 }
      };
    }
  }

  // Get location parameters from localStorage (same as home page)
  getLocationParams() {
    try {
      const savedLocation = localStorage.getItem('userLocation');
      const savedAddress = localStorage.getItem('userAddress');
      
      if (savedLocation) {
        const locationData = JSON.parse(savedLocation);
        if (locationData.latitude && locationData.longitude) {
          return {
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            address: savedAddress || 'Current Location'
          };
        }
      }
    } catch (error) {
      console.error('Error parsing saved location for RAG:', error);
    }
    
    // Return empty object if no location available
    return {};
  }

  // Clean up old cache entries
  cleanupCache() {
    const now = Date.now();
    const keysToDelete = [];
    
    this.cache.forEach((value, key) => {
      if (now - value.timestamp > this.cacheTimeout) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
    });
  }
}

// Export singleton instance
export default new RAGService();
